import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Star, Heart, Zap } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { useAuth } from "../../context/AuthContext";
import { formatPrice } from "../../utils/format";
import toast from "react-hot-toast";

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const wishlisted = isWishlisted(product.id);
  const [hovered, setHovered] = useState(false);
  const [adding, setAdding] = useState(false);

  async function handleAdd(e) {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock <= 0) return;
    setAdding(true);
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || "",
      category: product.category,
    });
    toast.success(`Added to cart ✓`, { duration: 1500 });
    setTimeout(() => setAdding(false), 600);
  }

  async function handleWishlist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Sign in to save items"); return; }
    const added = await toggleWishlist(product.id);
    toast.success(added ? "Saved to wishlist ❤️" : "Removed from wishlist", { duration: 1500 });
  }

  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;
  const isLowStock = product.stock > 0 && product.stock <= 5;
  const outOfStock = product.stock <= 0;

  return (
    <Link to={`/product/${product.id}`} style={{ textDecoration: "none", display: "block" }}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: "#fff",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          overflow: "hidden",
          transition: "all 0.3s var(--ease)",
          boxShadow: hovered ? "0 12px 40px rgba(13,27,42,0.13)" : "0 1px 3px rgba(13,27,42,0.06)",
          transform: hovered ? "translateY(-6px)" : "translateY(0)",
          borderColor: hovered ? "var(--border-mid)" : "var(--border)",
          position: "relative",
        }}
      >
        {/* ── Image ─────────────────────────────────────────────────────── */}
        <div style={{ position: "relative", aspectRatio: "4/5", overflow: "hidden", background: "var(--surface)" }}>
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              loading="lazy"
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                transition: "transform 0.5s var(--ease)",
                transform: hovered ? "scale(1.07)" : "scale(1)",
              }}
            />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48, background: "var(--surface)" }}>
              📦
            </div>
          )}

          {/* Overlay on hover */}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to top, rgba(13,27,42,0.5) 0%, transparent 50%)",
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.3s var(--ease)",
            pointerEvents: "none",
          }} />

          {/* Top badges */}
          <div style={{ position: "absolute", top: 10, left: 10, display: "flex", flexDirection: "column", gap: 5, zIndex: 2 }}>
            {discount && (
              <span style={{
                background: "var(--brand)",
                color: "#fff", fontSize: 11, fontWeight: 700,
                padding: "4px 9px", borderRadius: "var(--radius-full)",
                boxShadow: "0 2px 8px var(--brand-glow)",
              }}>
                -{discount}%
              </span>
            )}
            {product.featured && !discount && (
              <span style={{
                background: "var(--gold)", color: "#fff",
                fontSize: 11, fontWeight: 700,
                padding: "4px 9px", borderRadius: "var(--radius-full)",
              }}>
                ⭐ Featured
              </span>
            )}
            {isLowStock && (
              <span style={{
                background: "var(--warning-bg)", color: "var(--warning)",
                fontSize: 10, fontWeight: 700, padding: "4px 9px",
                borderRadius: "var(--radius-full)", border: "1px solid var(--warning)",
              }}>
                🔥 {product.stock} left
              </span>
            )}
            {outOfStock && (
              <span style={{
                background: "var(--danger-bg)", color: "var(--danger)",
                fontSize: 10, fontWeight: 700, padding: "4px 9px",
                borderRadius: "var(--radius-full)", border: "1px solid var(--danger)",
              }}>
                Out of Stock
              </span>
            )}
          </div>

          {/* Wishlist */}
          <button
            onClick={handleWishlist}
            aria-label="Toggle wishlist"
            style={{
              position: "absolute", top: 10, right: 10, zIndex: 2,
              width: 34, height: 34, borderRadius: "50%",
              background: wishlisted ? "#FEE2E2" : "rgba(255,255,255,0.92)",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(8px)", boxShadow: "var(--shadow-sm)",
              transition: "all 0.25s var(--ease-spring)",
              transform: hovered ? "scale(1)" : "scale(0.88)",
              opacity: hovered || wishlisted ? 1 : 0,
            }}
          >
            <Heart size={15} fill={wishlisted ? "#DC2626" : "none"} color={wishlisted ? "#DC2626" : "var(--text-muted)"} />
          </button>

          {/* Bottom hover action bar */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 2,
            padding: "12px",
            transform: hovered ? "translateY(0)" : "translateY(100%)",
            opacity: hovered ? 1 : 0,
            transition: "all 0.3s var(--ease)",
          }}>
            <button
              onClick={handleAdd}
              disabled={outOfStock || adding}
              style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "10px", borderRadius: "var(--radius-sm)",
                background: adding ? "var(--success)" : outOfStock ? "rgba(107,114,128,0.9)" : "rgba(255,255,255,0.95)",
                color: adding ? "#fff" : outOfStock ? "#fff" : "var(--brand)",
                border: "none", fontSize: 13, fontWeight: 700, cursor: outOfStock ? "not-allowed" : "pointer",
                backdropFilter: "blur(8px)",
                boxShadow: "0 4px 16px rgba(13,27,42,0.2)",
                transition: "all 0.2s var(--ease)",
              }}
            >
              {adding ? (
                <><Zap size={14} fill="currentColor" /> Added!</>
              ) : outOfStock ? (
                "Out of Stock"
              ) : (
                <><ShoppingCart size={14} /> Add to Cart</>
              )}
            </button>
          </div>
        </div>

        {/* ── Info ──────────────────────────────────────────────────────── */}
        <div style={{ padding: "14px 16px 16px" }}>
          {/* Category */}
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--brand)", marginBottom: 5 }}>
            {product.category}{product.subcategory ? ` › ${product.subcategory}` : ""}
          </p>

          {/* Name */}
          <h3 style={{
            fontSize: 14, fontFamily: "var(--font-head)", fontWeight: 600,
            lineHeight: 1.35, marginBottom: 8, color: "var(--text)",
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {product.name}
          </h3>

          {/* Rating */}
          {product.rating > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 1 }}>
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={11} fill={s <= Math.round(product.rating) ? "#F5A623" : "none"} color={s <= Math.round(product.rating) ? "#F5A623" : "var(--border)"} />
                ))}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>{product.rating}</span>
              <span style={{ fontSize: 11, color: "var(--text-faint)" }}>({product.reviewCount || 0})</span>
            </div>
          )}

          {/* Price row */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8 }}>
            <div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 800, color: "var(--text)", lineHeight: 1 }}>
                {formatPrice(product.price)}
              </div>
              {product.originalPrice && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                  <span style={{ fontSize: 12, color: "var(--text-faint)", textDecoration: "line-through" }}>
                    {formatPrice(product.originalPrice)}
                  </span>
                  {discount && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "var(--success)" }}>
                      Save {formatPrice(product.originalPrice - product.price)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Quick add (visible when not hovered) */}
            <button
              onClick={handleAdd}
              disabled={outOfStock}
              aria-label="Add to cart"
              style={{
                width: 36, height: 36, borderRadius: "var(--radius-sm)",
                background: outOfStock ? "var(--surface)" : "var(--brand)",
                color: outOfStock ? "var(--text-muted)" : "#fff",
                border: "none", display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, cursor: outOfStock ? "not-allowed" : "pointer",
                boxShadow: outOfStock ? "none" : "0 4px 12px var(--brand-glow)",
                transition: "all 0.2s var(--ease-spring)",
                transform: adding ? "scale(0.9)" : "scale(1)",
              }}
            >
              <ShoppingCart size={15} />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
